import { _ } from "vue-underscore";
import { EventBus } from "@/plugins/bus";
import { PRODUCT_DEFAULTS } from "@/constants";
import {
  OK,
  FLOW_MAP_TABLE
} from "@/constants";
import { mapActions, mapMutations } from "vuex";
import { loadArticleOrderData } from "@/utils";
const curtainEvent = 'load-curtain-article-to-flow'

export const EditPropertyMixin = {
  props: {
    isEdit: {
      type: Boolean,
      default: false
    }
  }
}

export const EditFlowComponentMixin = {
  mixins: [EditPropertyMixin],
  data() {
    return {
      flowData: {},
      loadDataWaiterKey: 'loadingFlowData'
    }
  },
  methods: {
    mapDataBasedOnMapTable(apiData) {
      return _.pick(
        _.mapObject(
          FLOW_MAP_TABLE,
          (step) => {
            let stepData = {}
            const properties = _.filter(
              step, questionKey => apiData[questionKey] !== undefined
            )
            properties.forEach(questionKey => {
              stepData[questionKey] = apiData[questionKey]
            })
            return stepData
          }
        ),
        (step) => !_.isEmpty(step)
      )
    },
    async loadFlowApiData(apiData) {
      await this.$nextTick()
      const flowData = this.mapDataBasedOnMapTable(apiData)
      if (process.env.NODE_ENV !== 'production') {
        console.log("flow data will be loaded ", flowData)
      }
      this.flowData = flowData
      // wait DOM transformation
      // and after enable auto fetching prices
      await this.$nextTick()
      this.$wait.end(this.loadDataWaiterKey)
      EventBus.$emit('on-flow-data-loaded')
    }
  }
}

// The mixin which related to the logic
// about loading and transforming data from api
// and a right-way putting the result as value into flow-generator
const BaseFlowEditMixin = {
  props: {
    articleOrderId: {
      type: [String, Number],
      default: null
    },
  },
  data() {
    return {
      editStepIndex: 1
    }
  },
  methods: {
    ...mapMutations({
      disablePriceFetch: 'order/disablePriceFetch',
      enablePriceFetch: 'order/enablePriceFetch',
    }),
    async loadArticleData(orderNumber) {
      const { data, status } = await loadArticleOrderData({
        orderNumber
      });
      if (status === OK && data.articles.length > 0) {
        return data.articles[0]
      }
      return null
    },
    createFlowEditBusEvents(flowInfo) {
      // we could set data to flow after complexity processing
      // and when product template was loaded
      // that's why we use subscriptions on the EventBus
      // and control loading data process with $emit
      // when we know that all stuff is ready for that
      const curtainFlowRef = this.$refs.curtainFlow
      const isCurtainEdit = curtainFlowRef && flowInfo

      const createFlowBusEvent = (eventKey, data, flow) => {
        EventBus.$on(eventKey, async function () {
          await flow.loadFlowApiData(data)
          EventBus.$off(eventKey)
        })
      }
      // catch prepared data and initialized curtain vuex stuff
      // and load api data into flow
      createFlowBusEvent(curtainEvent, flowInfo, curtainFlowRef)
      // when all the flows data were loaded to the flow components
      // we could enable prices auto-fetching and
      // make first prices fetch for each flow
      EventBus.$on('on-flow-data-loaded', function () {
        setTimeout(async () => {
          this.enablePriceFetch()
          if (isCurtainEdit) {
            await this.$nextTick()
            curtainFlowRef.openAllPanels()
            curtainFlowRef.validateSteps()
            await this.debouncedFetchCurtainPriceData()
            await this.debouncedFetchRailRodPriceData()
          }
          // we use url query param 'step' to activate
          // curtain or railrod step after data loaded
          // default is curtain step
          const stepIndex = this.$route.query.step || 1
          this.activateStep(stepIndex)
          this.$wait.end("CURTAIN_DATA_LOAD_WAITER")
          EventBus.$off('on-flow-data-loaded');
        }, 2000)
      }.bind(this))
    },
    // some values, like float, came from backend as decimal in string format
    // we must transform it to Number for well-working in the FlowGenerator
    transformPropertyValue(propKey, propValue) {
      if (propKey === 'wave_runner_distance') {
        return parseFloat(propValue).toFixed(1)
      }
      const decimals = [
        'width', 'height', 'hem_size', 'wave_length',
        'left_side_of_width', 'right_side_of_width',
        'assembly_height', 'length'
      ]
      if (decimals.includes(propKey)) {
        return parseFloat(propValue)
      }
      return propValue
    },
  },
  beforeDestroy() {
    EventBus.$off(curtainEvent);
    EventBus.$off('on-flow-data-loaded');
  }
}

export const CurtainFlowEditMixin = {
  mixins: [BaseFlowEditMixin],
  data() {
    return {
      curtainFabricUuid: null,
      editStepIndex: 1
    }
  },
  computed: {
    fabrics() {
      return this.$store.getters['fabrics/getFabrics']
    },
    isLoading() {
      return this.$wait.is('isLoadingCurtainTemplate')
        || this.$wait.is('isLoadingFabrics')
        || this.$wait.is('CURTAIN_DATA_LOAD_WAITER')
    }
  },
  watch: {
    'fabrics': async function (val) {
      if (val.length > 0) await this.loadCurtainEdit()
    }
  },
  methods: {
    ...mapMutations({
      setCurtainOrderNumber: 'order/setCurtainOrderNumber',
      setCurtainParameters: 'order/setCurtainParameters',
      setCurtainType: 'order/setCurtainType',
      setRailRodOrderNumber: 'order/setRailRodOrderNumber',
      setRailRodParameters: 'order/setRailRodParameters',
    }),
    ...mapActions({
      setCurtainType: 'order/setCurtainType',
      setCurtainPriceData: 'order/setCurtainPriceData',
      setWithRailRod: 'order/setWithRailRod',
      loadAndSetFabric: 'order/loadAndSetFabric',
      resetState: 'order/resetState',
      setRoom: 'order/setRoom',
      setRailRodType: 'order/setRailRodType',
      setRailRodPriceData: 'order/setRailRodPriceData',
      getFabricByArticleNumber: 'fabrics/getFabricByArticleNumber',
      addCurtainParameter: 'order/addCurtainParameter',

    }),
    prepareApiInfo(apiData) {
      return _.mapObject(
        _.pick(apiData, val => !["", null, "0.0"].includes(val)),
        (value, key) => this.transformPropertyValue(key, value)
      )
    },
    prepareRailRodInfo(railRodApiData) {
      return _.mapObject(
        _.pick(
          railRodApiData,
          (val, key) => ['width', 'support_type', 'color'].includes(key)
        ),
        (value, key) => this.transformPropertyValue(key, value)
      )
    },
    async prepareRailRodEdit(articleData) {
      // unpack api data to constants
      const type = articleData.type
      const railRodInfo = this.prepareRailRodInfo(articleData)
      await this.setRailRodType(type)
      await this.setRailRodParameters(railRodInfo)
      return { railRodInfo }
    },
    async loadCurtainData() {
      this.setCurtainOrderNumber(this.articleOrderId)
      return await this.loadArticleData(this.articleOrderId)
    },
    async prepareCurtainEdit(articleData) {
      this.$wait.start("CURTAIN_DATA_LOAD_WAITER")
      // unpack api data
      const { type, accessory_order, order_number, main_fabric_uuid } = articleData
      await this.setCurtainType(type)
      // use only required parameters
      const curtainInfo = this.prepareCurtainInfo(articleData)
      await this.$nextTick()
      // preparing and set curtain stuff
      await this.loadAndSetFabric(main_fabric_uuid)
      await this.setRoom(curtainInfo.room)
      await this.setCurtainParameters(curtainInfo)
      await this.setWithRailRod(!!accessory_order)

      this.setCurtainOrderNumber(order_number)
      this.addCurtainParameter(PRODUCT_DEFAULTS[type])
      // processing related articles, like rail/rod/pullrod
      if (accessory_order) {
        await this.setRailRodOrderNumber(accessory_order.order_number)
        const { railRodInfo } = await this.prepareRailRodEdit(accessory_order)
        return { ...curtainInfo, ...railRodInfo }
      }
      return { ...curtainInfo }
    },
    async loadCurtainEdit() {
      const data = await this.loadCurtainData()
      if (data) {
        const articles = await this.prepareCurtainEdit(data)
        this.createFlowEditBusEvents(articles)
      }
    }
  },
}
